/**
 * University Pages Manager - CMS Admin Panel
 * Features:
 * - List all university pages with status
 * - Super Admin: Regenerate content button
 * - View stale content pages
 * - Bulk refresh stale content
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  RefreshCw,
  Eye,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Globe,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const UniversityPagesManager = () => {
  const { token, user, logout } = useCMSAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);
  
  // Regeneration state
  const [regenerating, setRegenerating] = useState({});
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  
  // Bulk refresh state
  const [bulkRefreshing, setBulkRefreshing] = useState(false);
  const [stalePages, setStalePages] = useState([]);

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  useEffect(() => {
    fetchPages();
    fetchStats();
    if (isSuperAdmin) {
      fetchStalePages();
    }
  }, [page, searchQuery, statusFilter, countryFilter]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(countryFilter !== 'all' && { country: countryFilter }),
        ...(searchQuery && { search: searchQuery })
      });

      const res = await fetch(`${API_URL}/api/university-pages/cms/list?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (handleUnauthorized(res)) return;

      if (res.ok) {
        const data = await res.json();
        setPages(data.pages || []);
        setTotalPages(data.pages_count || 1);
      } else {
        toast.error('Failed to fetch university pages');
      }
    } catch (e) {
      console.error('Error fetching pages:', e);
      toast.error('Failed to fetch university pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/university-pages/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  };

  const fetchStalePages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-content/stale-content?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        const data = await res.json();
        setStalePages(data.stale_pages || []);
      }
    } catch (e) {
      console.error('Error fetching stale pages:', e);
    }
  };

  const handleRegenerate = async (pageData) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admins can regenerate content');
      return;
    }

    setRegenerating(prev => ({ ...prev, [pageData.id]: true }));
    
    try {
      const res = await fetch(`${API_URL}/api/ai-content/regenerate/${pageData.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        toast.success(`Content regeneration started for ${pageData.university_name}`);
        // Poll for status
        pollRegenerationStatus(pageData.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to start regeneration');
        setRegenerating(prev => ({ ...prev, [pageData.id]: false }));
      }
    } catch (e) {
      toast.error('Failed to regenerate content');
      setRegenerating(prev => ({ ...prev, [pageData.id]: false }));
    }
    
    setRegenerateDialogOpen(false);
    setSelectedPage(null);
  };

  const pollRegenerationStatus = async (pageId) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 seconds max
    
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ai-content/regeneration-status/${pageId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          
          if (data.regeneration_status === 'completed') {
            toast.success(`Content regenerated for ${data.university_name}`);
            setRegenerating(prev => ({ ...prev, [pageId]: false }));
            fetchPages();
            return;
          } else if (data.regeneration_status === 'failed') {
            toast.error(`Regeneration failed: ${data.regeneration_error || 'Unknown error'}`);
            setRegenerating(prev => ({ ...prev, [pageId]: false }));
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          toast.info('Regeneration is taking longer than expected. Check back later.');
          setRegenerating(prev => ({ ...prev, [pageId]: false }));
        }
      } catch (e) {
        setRegenerating(prev => ({ ...prev, [pageId]: false }));
      }
    };
    
    poll();
  };

  const handleBulkRefresh = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admins can refresh stale content');
      return;
    }

    setBulkRefreshing(true);
    
    try {
      const res = await fetch(`${API_URL}/api/ai-content/refresh-stale-content`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ limit: 10 })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchStalePages();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to start bulk refresh');
      }
    } catch (e) {
      toast.error('Failed to start bulk refresh');
    } finally {
      setBulkRefreshing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      pending_review: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const isPageStale = (pageData) => {
    if (!pageData.content_expires_at) return false;
    return new Date(pageData.content_expires_at) < new Date();
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="university-pages-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal">University Pages</h1>
            <p className="text-ux-charcoal/60">
              Manage AI-generated university detail pages
            </p>
          </div>
          
          {isSuperAdmin && stalePages.length > 0 && (
            <Button 
              onClick={handleBulkRefresh}
              disabled={bulkRefreshing}
              className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white"
              data-testid="bulk-refresh-btn"
            >
              {bulkRefreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh Stale Content ({stalePages.length})
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-ux-charcoal">{stats.total}</div>
                <div className="text-sm text-ux-charcoal/60">Total Pages</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{stats.by_status?.approved || 0}</div>
                <div className="text-sm text-ux-charcoal/60">Published</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.by_status?.pending_review || 0}</div>
                <div className="text-sm text-ux-charcoal/60">Pending Review</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-600">{stats.by_status?.draft || 0}</div>
                <div className="text-sm text-ux-charcoal/60">Draft</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-charcoal/50" />
                <Input
                  placeholder="Search universities..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>

              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Published</SelectItem>
                  <SelectItem value="pending_review">Pending</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Pages Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
              </div>
            ) : pages.length === 0 ? (
              <div className="text-center py-12 text-ux-charcoal/60">
                No university pages found
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>University</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Views</TableHead>
                    <TableHead>Content Age</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pages.map((pageData) => (
                    <TableRow key={pageData.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium">{pageData.university_name}</div>
                            <div className="text-xs text-ux-charcoal/50">{pageData.slug}</div>
                          </div>
                          {pageData.partner_tier === 'direct' && (
                            <Badge className="bg-ux-green text-white text-xs">Direct</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Globe className="w-4 h-4 text-ux-charcoal/50" />
                          {pageData.country}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(pageData.status)}>
                          {pageData.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4 text-ux-charcoal/50" />
                          {pageData.views?.toLocaleString() || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        {pageData.content_generated_at ? (
                          <div className="flex items-center gap-1">
                            {isPageStale(pageData) ? (
                              <>
                                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                <span className="text-yellow-600 text-sm">Stale</span>
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                <span className="text-green-600 text-sm">Fresh</span>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-ux-charcoal/50 text-sm">N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {pageData.status === 'approved' && (
                            <Link to={`/universities/${pageData.slug}`} target="_blank">
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                          
                          {isSuperAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedPage(pageData);
                                setRegenerateDialogOpen(true);
                              }}
                              disabled={regenerating[pageData.id]}
                              data-testid={`regenerate-btn-${pageData.id}`}
                            >
                              {regenerating[pageData.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4 mr-1" />
                                  Regenerate
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-ux-charcoal/60">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}

        {/* Regenerate Confirmation Dialog */}
        <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Regenerate Content</DialogTitle>
              <DialogDescription>
                This will use AI to regenerate all content for <strong>{selectedPage?.university_name}</strong>. 
                The existing content will be replaced with fresh, updated information.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRegenerateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleRegenerate(selectedPage)}
                className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Regenerate Content
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UniversityPagesManager;
